import { useI18nTypes } from '@/composables'
import { PUBLIC_URL_REG_EXP } from '@/const'
import router from '@/router'
import {
  useWalletAPIStore,
  useNFTAPIStore,
  useInfoAPIStore,
  useLocaleStore,
  useUserAPIStore,
  useWalletStore,
  useNFTContractAPIStore,
  useLoadingsStore,
  useCoinPriceAPIStore,
} from '@/stores'
import { isExpiredRefreshToken } from '@/utils'

export function useFetchHandlers() {
  const walletAPIStore = useWalletAPIStore()
  const walletStore = useWalletStore()
  const nftAPIStore = useNFTAPIStore()
  const nftContractAPIStore = useNFTContractAPIStore()
  const infoAPIStore = useInfoAPIStore()
  const userAPIStore = useUserAPIStore()
  const localeStore = useLocaleStore()
  const loadingsStore = useLoadingsStore()

  const { locale: global } = useI18nTypes()

  const loginSuccessHandler = async () => {
    if (PUBLIC_URL_REG_EXP.test(window.location.pathname)) {
      return
    }

    if (isExpiredRefreshToken()) {
      await userAPIStore.logout()
      return router.push('/signin')
    }

    try {
      await Promise.all([
        walletAPIStore.fetchWalletList(),
        userAPIStore.getUserDetail(),
      ])

      // 모든 지갑의 PIN 잠금 상태 확인
      const { usePinTryCount } = await import('./usePinTryCount')
      const { requestPinTryCount } = usePinTryCount()
      const pinLockChecks = walletAPIStore.walletList.map((wallet) =>
        requestPinTryCount(wallet.id),
      )
      await Promise.all(pinLockChecks)
    } catch (error) {
      throw error // 에러를 던져서 상위에서 처리하도록
    }

    if (walletAPIStore.walletList.length === 0) {
      router.push({
        name: 'signup_with_email_set_pin',
        query: {
          email: userAPIStore.userInfo?.email as string,
        },
      })
      return
    }

    try {
      await nftAPIStore.fetchSupportedNFTList()
    } catch (error) {
      // NFT 지원 목록 조회 실패는 무시
    }

    try {
      await infoAPIStore.fetchAllInfo()

      await Promise.all([
        walletAPIStore.fetchAllWalletDetail(true),
        localeStore.getLocaleInfo(),
      ])

      // V2: CoinGecko 가격 정보 조회
      try {
        const coinPriceAPIStore = useCoinPriceAPIStore()

        // 모든 account의 coinGeckoId 수집
        const coinGeckoIds = new Set<string>()
        walletStore.accountList.forEach((account) => {
          if (account.coin?.coinGeckoId) {
            coinGeckoIds.add(account.coin.coinGeckoId)
          }
        })
        walletStore.accountTokenList.forEach((token) => {
          if (token.coin?.coinGeckoId) {
            coinGeckoIds.add(token.coin.coinGeckoId)
          }
        })

        if (coinGeckoIds.size > 0) {
          // 타임아웃 설정 (10초) - CoinGecko 응답이 느릴 경우 대비
          const fetchPriceWithTimeout = Promise.race([
            coinPriceAPIStore.fetchCoinPrice({
              coinGeckoIds: Array.from(coinGeckoIds),
              currency: infoAPIStore.currentCurrency,
            }),
            new Promise((_, reject) =>
              setTimeout(
                () => reject(new Error('CoinGecko price fetch timeout (10s)')),
                10000,
              ),
            ),
          ])

          await fetchPriceWithTimeout
        }
      } catch (error) {
        // 가격 정보는 필수가 아니므로 에러를 무시하고 계속 진행
      }

      // url이 Droplink인 경우 locale은 변경하지 않는다.
      if (window.location.pathname.includes('/droplink')) {
        return
      }

      // 사용자 설정에 맞게 vue-i18n locale 동기화
      const userLocale = infoAPIStore.localeList?.find(
        (locale) => locale.id === userAPIStore.userInfo?.localeId,
      )
      if (userLocale) {
        const i18nLocaleMap: { [key: string]: string } = {
          KR: 'ko',
          US: 'en',
          JP: 'ja',
        }
        const targetLocale = i18nLocaleMap[userLocale.localeGroup]
        if (targetLocale && global.value !== targetLocale) {
          global.value = targetLocale
        }
      }
    } catch (error) {
      throw error // V2에서는 에러를 던져서 재시도를 방지
    }

    return ''
  }

  const publicUrlLoginSuccessHandler = async () => {
    if (!userAPIStore.isLoginUser()) return
    await Promise.all([
      walletAPIStore.fetchWalletList(),
      userAPIStore.getUserDetail(),
    ])
  }

  const nftFecthAllHandler = async ({ force = false, recursion = false }) => {
    try {
      if (force) {
        await Promise.all(
          walletStore.walletList.map((wallet) => {
            return wallet.fetchAccounts(force)
          }),
        )
      }

      // V2: 각 wallet별로 NFT 목록 조회 (DB 데이터)
      await Promise.all(
        walletStore.walletList.map((wallet) => {
          return nftAPIStore.fetchNFTList({
            walletId: wallet.id,
            force: true,
          })
        }),
      )

      // V2: NFTContract는 별도 API 없음 (NFT 응답에 포함)
      const ids = walletStore.nftList
        .map((nft) => nft.nftContractId as number)
        .filter((id) => id > 0) // V2는 nftContractId=0
      if (ids.length > 0) {
        await nftContractAPIStore.fetchNFTContractList({ ids, force: true })
      }

      loadingsStore.closeSpinner()

      // 재귀 호출 방지
      if (recursion) return

      // V2: refreshNFTList는 walletId 사용 (accountIds 아님)
      // 각 wallet별로 네트워크에서 새로고침 후 목록 다시 조회
      await Promise.all(
        walletStore.walletList.map(async (wallet) => {
          // V2: refreshNFTs를 호출 (walletId 사용)
          await nftAPIStore.refreshNFTList({
            accountIds: walletStore.accountList
              .filter((acc) => acc.walletId === wallet.id)
              .map((account) => account.id),
            walletId: wallet.id, // 명시적으로 walletId 전달
          })

          // 새로고침 후 업데이트된 목록 다시 조회
          await nftAPIStore.fetchNFTList({
            walletId: wallet.id,
            force: true,
          })
        }),
      )
    } catch (error) {
      // NFT handler error ignored
    }
  }

  const nftFecthHandler = async (force = false) => {
    const fecthHistory = nftAPIStore.requestFetchHistory?.indexOf(
      walletStore.selectedWalletId,
    ) as number

    if (!force && fecthHistory !== -1) return
    try {
      const wallet = walletStore.getWallet(walletStore.selectedWalletId)
      if (!wallet) return

      // V2: 먼저 DB에서 NFT 목록 조회 (빠름)
      await nftAPIStore.fetchNFTList({
        walletId: wallet.id as string,
        force: force,
      })

      // V2: 네트워크에서 새로고침 (느림) - 백그라운드 실행
      nftAPIStore
        .refreshNFTList({
          accountIds: wallet.accounts.map((account) => account.id),
          walletId: wallet.id as string,
        })
        .then(() => {
          // send flow 중에는 NFT 목록을 갱신하지 않음 (ID가 변경되면 기존 query의 nftId가 무효화됨)
          const currentRouteName = router.currentRoute.value.name as
            | string
            | undefined
          if (currentRouteName?.startsWith('send_nft')) {
            return
          }
          // 새로고침 완료 후 목록 다시 조회
          nftAPIStore.fetchNFTList({
            walletId: wallet.id as string,
            force: true,
          })
        })
        .catch(() => {
          // NFT refresh error ignored
        })

      // V2: NFTContract는 필요시에만 조회
      const ids = walletStore.nftList
        .map((nft) => nft.nftContractId as number)
        .filter((id) => id > 0)
      if (ids.length > 0) {
        await nftContractAPIStore.fetchNFTContractList({
          ids: ids,
          force: force,
        })
      }
    } catch (error) {
      // NFT handler error ignored
    }
  }

  return {
    loginSuccessHandler,
    publicUrlLoginSuccessHandler,
    nftFecthAllHandler,
    nftFecthHandler,
  }
}
